import * as React from "react";
import { useTranslation } from "react-i18next";
import { NodeModel, Tree } from "@minoru/react-dnd-treeview";

import Box from "@mui/system/Box";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CancelIcon from "@mui/icons-material/Cancel";

import useCourse from "src/hooks/useCourse";
import useNewQuestion from "src/hooks/useNewQuestion";

import Loader from "./Loader";
import AddCategoryBottomSheet from "./AddCategoryBottomSheet";
import QuestionTagTreeItem from "src/components/Questions/QuestionTagTreeItem";

import { categorySearcher } from "src/helpers/common";

import styles from "./AITagsSelection.module.css";

const AITagsSelection = ({
  addTags,
  courseId,
  courseCode = "",
  selectedTags,
  handleDelete,
  required = false,
}) => {
  const { t } = useTranslation();
  const {
    loaders,
    courses,
    categories,
    updateCategoryLocally,
    addNewlyCreatedCategory,
  } = useCourse();
  const { questionData } = useNewQuestion();

  const [tagNames, setTagNames] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [treeData, setTreeData] = React.useState<NodeModel[]>([]);

  const calculateChildren = (node: NodeModel) =>
    treeData?.filter((d) => d.parent === node.id).length || 0;

  // Setting course categories to display in tree
  React.useEffect(() => {
    const courseIdentifier = courses?.find(
      (c) => c?.courseCode === questionData?.courseId
    )?.id;
    setTreeData(categories[courseIdentifier ?? courseId]?.categories ?? []);
  }, [categories, courseId, questionData, courses]);

  // Setting selected categories to display tag pills
  React.useEffect(() => {
    const categories = treeData?.filter((tag) =>
      selectedTags.includes(tag?.id)
    );
    setTagNames(categories);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTags, courseId, treeData]);

  React.useEffect(() => {
    categorySearcher(
      searchQuery,
      setTreeData,
      categories[courseId]?.categories ?? []
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, categories, courseId]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleAddCategoriesSheet = (event: any) => {
    if (event) {
      event?.stopPropagation();
    }
    const bottomSheet = document.getElementById("category-btm-sheet");
    if (bottomSheet) {
      bottomSheet.style.display = "block";
    }
    setTimeout(() => {
      document
        .getElementById("category-btm-sheet")
        ?.classList.add("active-blk");
    }, 10);
  };

  return (
    <Box className="quest-tag-blk category-wrap">
      <Box
        display={"flex"}
        flexDirection={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography variant="subtitle2" component="h5">
          {t("course.aiTagsSelection.aiTags")}{" "}
          {required ? <span className="text-red">*</span> : null}
        </Typography>

        {courseCode ? (
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={handleAddCategoriesSheet}
            className="text-capitalize btn-text"
          >
            {t("course.aiTagsSelection.addNewTag")}
          </Button>
        ) : null}
      </Box>

      <Box className="custom-input-wrap bg-light">
        <Typography variant="subtitle2" component="h5">
          {t("course.aiTagsSelection.searchTags")}
        </Typography>
        <TextField
          size="small"
          variant="outlined"
          style={{ width: "100%" }}
          onChange={(e) => setSearchQuery(e?.target?.value)}
        />
      </Box>

      <Box className="question-tree">
        {loaders?.categories ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Loader />
          </Box>
        ) : treeData?.length ? (
          <Tree
            rootId={0}
            tree={treeData}
            render={(node: NodeModel, { depth, isOpen, onToggle }) => (
              <QuestionTagTreeItem
                node={node}
                depth={depth}
                isOpen={isOpen}
                onToggle={onToggle}
                setQuestionTags={addTags}
                selectedTags={selectedTags}
                nodeId={node.id.toString()}
                childs={calculateChildren(node).toString()}
              />
            )}
            onDrop={null}
            classes={{
              root: styles.treeRoot,
              dropTarget: styles.dropTarget,
              draggingSource: styles.draggingSource,
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="caption">
              {t("course.aiTagsSelection.emptyTags")}
            </Typography>
          </Box>
        )}
      </Box>

      {tagNames.length > 0 && (
        <Box className="selected-tags">
          {tagNames?.map((tag, index) => (
            <Chip
              key={index}
              label={tag.text}
              variant="outlined"
              className="tagChip"
              deleteIcon={<CancelIcon />}
              onClick={() => addTags(tag?.id)}
              onDelete={() => handleDelete(tag?.id)}
            />
          ))}
        </Box>
      )}

      <AddCategoryBottomSheet
        editingCategoryId={""}
        course={{ id: courseId, courseCode }}
        currentCategories={categories?.[courseId]?.categories}
        selectedCategory={{ categoryId: "", categoryDepth: 0 }}
        updateCategoryLocally={updateCategoryLocally}
        addNewlyCreatedCategory={addNewlyCreatedCategory}
      />
    </Box>
  );
};

export default AITagsSelection;
